
import { useQuery } from "@tanstack/react-query";
import { Scroll, Target, Heart, AlertCircle } from "lucide-react";
import { ArticleCard } from "./articles/ArticleCard";
import { LoadingState } from "./articles/LoadingState";
import { ErrorState } from "./articles/ErrorState";
import { fetchArticles } from "@/utils/articleUtils";

const iconMap = {
  "foundational-concepts": Scroll,
  "journey-stages": Target,
  "signs-connections": Heart,
  "spiritual-esoteric": AlertCircle,
};

export const LatestArticles = () => {
  const { data: articles, isLoading, error } = useQuery({
    queryKey: ["latest-articles"],
    queryFn: fetchArticles,
    retry: 3,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });

  const renderContent = () => {
    if (isLoading) return <LoadingState />;
    if (error) return <ErrorState />;
    if (!articles || articles.length === 0) {
      return (
        <div className="text-center py-8">
          <AlertCircle className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-xl font-medium text-gray-900 mb-2">No Articles Found</h3>
          <p className="text-gray-600 mb-4">
            Please refresh the page to try loading articles again.
          </p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-spirit-600 hover:bg-spirit-700 text-white px-4 py-2 rounded"
          >
            Refresh Page
          </button>
        </div>
      );
    }

    // Ensure unique articles by title
    const uniqueArticles = articles.reduce((acc, current) => {
      const x = acc.find(item => item.title === current.title);
      if (!x) {
        return acc.concat([current]);
      } else {
        return acc;
      }
    }, []);

    // Only take the first 4 articles
    const displayArticles = uniqueArticles.slice(0, 4);

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 justify-items-center">
        {displayArticles.map((article, index) => (
          <ArticleCard 
            key={article.id}
            article={article}
            index={index}
            iconMap={iconMap}
          />
        ))}
      </div>
    );
  };

  return (
    <section className="py-24 bg-gradient-to-b from-white to-spirit-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <span className="inline-block px-3 py-1 text-sm font-medium bg-spirit-100 text-spirit-700 rounded-full">
            Latest Articles
          </span>
        </div>
        {renderContent()}
      </div>
    </section>
  );
};
